'use client';
import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const PAIRS = [
{
  id: 1,
  painLabel: 'The old way',
  painTitle: 'Luck disappears into the void',
  painDesc: 'You have a wild lucky moment — miss a flight, find $200, land a dream job by chance — and it vanishes. No record, no pattern, no proof it even happened.',
  painImage: "https://images.unsplash.com/photo-1613164338996-da831f21bb48",
  painImageAlt: 'Person looking frustrated at a blank notebook, unable to capture a fleeting lucky moment',
  solLabel: 'With LuckCount',
  solTitle: 'Log it in 30 seconds. Own it forever.',
  solDesc: 'Post your incident, tag the type, add a short story. It joins the live global feed instantly — timestamped, searchable, and part of the world\'s largest luck dataset.',
  features: ['Anonymous or named posting', 'Incident type tagging', 'Live global feed', 'Personal luck history']
},
{
  id: 2,
  painLabel: 'Scattered across the internet',
  painTitle: 'Reddit threads. Twitter posts. Lost forever.',
  painDesc: 'Lucky stories get buried in subreddits, forgotten in tweet threads, or never shared at all. There\'s no single place the world tracks its luck.',
  painImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1990ad606-1771936644604.png",
  painImageAlt: 'Scattered browser tabs showing Reddit, Twitter, and forums with luck stories buried in noise',
  solLabel: 'With LuckCount',
  solTitle: 'One feed. Every lucky incident. Everywhere.',
  solDesc: 'From Lagos to Berlin to SF — unique visitors post their luck incidents in real-time. Browse by type, location, or tag. The world\'s luck, finally in one place.',
  features: ['Global live feed', 'Filter by luck type', 'Location tagging', 'Trending incident map']
}];


function PainCard({ image, imageAlt, label, title, desc

}: {image: string;imageAlt: string;label: string;title: string;desc: string;}) {
  return (
    <div className="relative rounded-2xl overflow-hidden" style={{ filter: 'saturate(0.25) brightness(0.65)' }}>
      <AppImage
        src={image}
        alt={imageAlt}
        className="w-full h-[280px] object-cover"
        width={600}
        height={280} />
      
      <div className="absolute inset-0 bg-gradient-to-t from-[#0D0F1C] via-[#0D0F1C]/60 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-6">
        <span className="text-[10px] font-display font-600 text-white/30 uppercase tracking-widest">{label}</span>
        <h3 className="text-xl font-display font-700 text-white/70 mt-1 mb-2">{title}</h3>
        <p className="text-sm text-white/40 font-body leading-relaxed">{desc}</p>
      </div>
    </div>);

}

function SolCard({ label, title, desc, features

}: {label: string;title: string;desc: string;features: string[];}) {
  return (
    <div
      className="relative rounded-2xl p-6 h-full"
      style={{
        background: 'rgba(255,255,255,0.07)',
        border: '1px solid rgba(61,255,196,0.2)',
        boxShadow: '0 0 40px rgba(61,255,196,0.06)'
      }}>
      
      <span className="badge-mint text-[10px] font-display font-700 px-2.5 py-1 rounded-full uppercase tracking-widest">{label}</span>
      <h3 className="text-2xl font-display font-700 text-iwhite mt-3 mb-3 leading-tight">{title}</h3>
      <p className="text-sm text-white/60 font-body leading-relaxed mb-5">{desc}</p>
      <div className="space-y-2.5">
        {features.map((f) =>
        <div key={f} className="flex items-center gap-2.5">
            <div
            className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(61,255,196,0.15)', border: '1px solid rgba(61,255,196,0.4)' }}>
            
              <svg width="8" height="8" viewBox="0 0 12 12" fill="none">
                <path d="M2 6l3 3 5-5" stroke="#3DFFC4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span className="text-sm text-white/70 font-body">{f}</span>
          </div>
        )}
      </div>

      <div
        className="absolute top-0 right-0 w-32 h-32 rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(61,255,196,0.12) 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />
      
    </div>);

}

export default function PainSolutionSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.12 }
    );
    const els = sectionRef.current?.querySelectorAll('.reveal-up, .reveal-left, .reveal-right');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-28 relative">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-20 reveal-up">
          <span className="badge-lilac text-xs font-display font-700 px-3 py-1.5 rounded-full uppercase tracking-wider">
            The Problem vs. The Fix
          </span>
          <h2 className="mt-4 text-[clamp(2rem,4vw,3rem)] font-display font-700 text-iwhite leading-tight tracking-tight">
            Lucky moments vanish.<br />
            <span style={{ color: '#3DFFC4' }}>Not anymore.</span>
          </h2>
        </div>

        <div className="space-y-16">
          {PAIRS.map((pair, i) =>
          <div key={pair.id} className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
              <div className={`reveal-${i % 2 === 0 ? 'left' : 'right'}`}>
                <PainCard
                image={pair.painImage}
                imageAlt={pair.painImageAlt}
                label={pair.painLabel}
                title={pair.painTitle}
                desc={pair.painDesc} />
              
              </div>
              <div className={`reveal-${i % 2 === 0 ? 'right' : 'left'} delay-200`}>
                <SolCard
                label={pair.solLabel}
                title={pair.solTitle}
                desc={pair.solDesc}
                features={pair.features} />
              
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}